/**
 * ProfilePage - User profile management page.
 *
 * Allows the authenticated user to view and edit their personal information,
 * change their password, manage notification preferences, and review read-only
 * account details.
 *
 * Sections:
 *	 1. Profile Header — avatar circle (first initial), full name, role badge, email
 *	 2. Personal Info Form — editable first_name, last_name, phone fields with Save
 *	 3. Change Password — current, new, confirm fields with visibility toggle and
 *		validation (12+ chars, mixed case, number, special char)
 *	 4. Notification Controls — iOS-style switch toggles for Work Order AND
 *		Inspection email notifications (assignment / completion / review request),
 *		with multi-select company/user watch-list dropdowns. Rendered data-driven
 *		from NOTIF_GROUPS so the two record types stay in lockstep.
 *	 5. Account Info — read-only display of role, company, auth provider, last login,
 *		and membership date
 *
 * On save: PATCH /auth/profile with updated fields. Success/error banner shown.
 * On password change: POST /auth/change-password with current + new passwords.
 * On notification save: PATCH /auth/notification-preferences with toggle/dropdown values.
 *
 * Accessible by: all authenticated users (protected by <ProtectedRoute>).
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { formatPhone } from '../../utils/formatPhone';
import { resolveStorageUrl } from '../../utils/resolveStorageUrl';
import { AvatarUpload } from '../../components/organisms/AvatarUpload';
import { Alert, Button, Checkbox, ToggleSwitch } from '../../components/atoms';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Shape of the personal info form fields */
interface ProfileFormData {
	firstName: string;
	lastName: string;
	phone: string;
	/** IANA timezone identifier for the user's local time (e.g. "America/New_York") */
	timezone: string;
}

/**
 * Common US timezone options for the profile timezone dropdown.
 * Values are IANA timezone identifiers used to convert UTC times
 * from the API into the user's local time for display.
 */
const TIMEZONE_OPTIONS = [
	{ value: 'America/New_York', label: 'Eastern Time (ET)' },
	{ value: 'America/Chicago', label: 'Central Time (CT)' },
	{ value: 'America/Denver', label: 'Mountain Time (MT)' },
	{ value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
	{ value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
	{ value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
	{ value: 'America/Phoenix', label: 'Arizona (no DST)' },
];

/** Shape of the change password form fields */
interface PasswordFormData {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

/** Shape of a password validation rule for the checklist UI */
interface PasswordRule {
	label: string;
	test: (pw: string) => boolean;
}

/**
 * Boolean notification-preference fields (simple on/off toggles).
 * Mirror the backend NotificationPreference columns one-for-one.
 */
type NotifBoolField =
	| 'wo_assigned_to_me'
	| 'wo_completed_by_me'
	| 'wo_review_requested'
	| 'insp_assigned_to_me'
	| 'insp_completed_by_me'
	| 'insp_review_requested';

/**
 * Array (watch-list) notification-preference fields. Each holds a list of
 * company_ids or user_ids the user subscribed to; null/empty = no subscription.
 */
type NotifArrayField =
	| 'wo_assigned_company'
	| 'wo_assigned_users'
	| 'wo_completed_company'
	| 'insp_assigned_company'
	| 'insp_assigned_users'
	| 'insp_completed_company';

/** Shape of the notification preferences returned from the API */
type NotificationPrefs = Record<NotifBoolField, boolean> & Record<NotifArrayField, number[] | null>;

/** Default preferences before the API responds — opt-out model (booleans true). */
const DEFAULT_NOTIF_PREFS: NotificationPrefs = {
	wo_assigned_to_me: true,
	wo_completed_by_me: true,
	wo_review_requested: true,
	insp_assigned_to_me: true,
	insp_completed_by_me: true,
	insp_review_requested: true,
	wo_assigned_company: null,
	wo_assigned_users: null,
	wo_completed_company: null,
	insp_assigned_company: null,
	insp_assigned_users: null,
	insp_completed_company: null,
};

/** A single rendered row in the notification controls — a bool toggle or a watch list. */
type NotifRow =
	| { type: 'bool'; field: NotifBoolField; label: string }
	| { type: 'watch'; field: NotifArrayField; label: string; options: 'company' | 'user' };

/**
 * Declarative layout for the notification controls. Work Orders and Inspections
 * share an identical structure, so they're described once each here and rendered
 * by the same code below — no copy-pasted JSX, and the two stay in sync.
 */
const NOTIF_GROUPS: { title: string; rows: NotifRow[] }[] = [
	{
		title: 'Work Orders',
		rows: [
			{ type: 'bool', field: 'wo_assigned_to_me', label: 'When a Work Order is assigned to me' },
			{ type: 'watch', field: 'wo_assigned_company', label: 'When a Work Order is assigned to anyone in', options: 'company' },
			{ type: 'watch', field: 'wo_assigned_users', label: 'When a Work Order is assigned to', options: 'user' },
			{ type: 'bool', field: 'wo_completed_by_me', label: 'When a Work Order I created is completed' },
			{ type: 'watch', field: 'wo_completed_company', label: 'When a Work Order is completed by', options: 'company' },
			{ type: 'bool', field: 'wo_review_requested', label: 'When a Work Order is submitted for my review' },
		],
	},
	{
		title: 'Inspections',
		rows: [
			{ type: 'bool', field: 'insp_assigned_to_me', label: 'When an Inspection is assigned to me' },
			{ type: 'watch', field: 'insp_assigned_company', label: 'When an Inspection is assigned to anyone in', options: 'company' },
			{ type: 'watch', field: 'insp_assigned_users', label: 'When an Inspection is assigned to', options: 'user' },
			{ type: 'bool', field: 'insp_completed_by_me', label: 'When an Inspection I created is completed' },
			{ type: 'watch', field: 'insp_completed_company', label: 'When an Inspection is completed by', options: 'company' },
			{ type: 'bool', field: 'insp_review_requested', label: 'When an Inspection is submitted for my review' },
		],
	},
];

/** The six watch-list array fields, derived from the group config (used for load + save). */
const NOTIF_ARRAY_FIELDS: NotifArrayField[] = NOTIF_GROUPS
	.flatMap((g) => g.rows)
	.filter((r): r is Extract<NotifRow, { type: 'watch' }> => r.type === 'watch')
	.map((r) => r.field);

/** Company option used in multi-select dropdowns */
interface CompanyOption {
	id: number;
	name: string;
}

/** User option used in multi-select dropdowns */
interface UserOption {
	id: number;
	first_name: string;
	last_name: string;
}

/* ============================================
 * PASSWORD VALIDATION RULES
 * ============================================ */

/** Password policy: 12+ chars, uppercase, lowercase, number, special character */
const PASSWORD_RULES: PasswordRule[] = [
	{ label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
	{ label: 'At least one uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
	{ label: 'At least one lowercase letter', test: (pw) => /[a-z]/.test(pw) },
	{ label: 'At least one number', test: (pw) => /[0-9]/.test(pw) },
	{ label: 'At least one special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

/* ============================================
 * HELPER: ROLE BADGE COLOR MAP
 * ============================================ */

/** Maps user roles to Tailwind badge color classes */
const ROLE_BADGE_COLORS: Record<string, string> = {
	admin: 'bg-red-100 text-red-800',
	developer: 'bg-purple-100 text-purple-800',
	manager: 'bg-blue-100 text-blue-800',
	technician: 'bg-green-100 text-green-800',
	reviewer: 'bg-yellow-100 text-yellow-800',
};

/* ============================================
 * SMALL PRESENTATIONAL HELPER: labelled row wrapping the ToggleSwitch atom
 * ============================================
 * The switch itself now lives in atoms/ToggleSwitch (which is also what the
 * watched-field rows below use); this keeps the label-left / switch-right row
 * layout the notification list wants.
 */
const NotifToggle: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
	<div className="profile-page__notif-toggle flex items-center justify-between">
		<span className="text-14px text-brand-text font-medium">{label}</span>
		<ToggleSwitch checked={checked} onChange={onChange} label={label} />
	</div>
);

/* ============================================
 * COMPONENT
 * ============================================ */

const ProfilePage: React.FC = () => {
	// Pull the current user from auth context
	const { user, updateUser } = useAuth();

	/* --- Personal info form state --- */
	const [profileForm, setProfileForm] = useState<ProfileFormData>({
		firstName: user?.firstName ?? '',
		lastName: user?.lastName ?? '',
		phone: user?.phone ?? '',
		timezone: user?.timezone ?? 'America/New_York',
	});
	const [profileSaving, setProfileSaving] = useState(false);
	const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

	/* --- Password form state --- */
	const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
		currentPassword: '',
		newPassword: '',
		confirmPassword: '',
	});
	const [passwordSaving, setPasswordSaving] = useState(false);
	const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

	/* --- Password visibility toggles --- */
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	/* --- Notification preferences state --- */
	/** The current notification preference values from the API */
	const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIF_PREFS);
	/** Per-watch-list ON state — controls whether the dropdown shows and whether
	 *  the array is sent on save (OFF => null, clearing the subscription). */
	const [watchOn, setWatchOn] = useState<Record<NotifArrayField, boolean>>(() =>
		Object.fromEntries(NOTIF_ARRAY_FIELDS.map((f) => [f, false])) as Record<NotifArrayField, boolean>,
	);
	/** Which watch-list dropdown is currently open (only one at a time), or null. */
	const [openDropdown, setOpenDropdown] = useState<NotifArrayField | null>(null);
	/** Saving state for the notification preferences form */
	const [notifSaving, setNotifSaving] = useState(false);
	/** Success/error feedback message for the notification preferences form */
	const [notifMessage, setNotifMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
	/** Whether notification preferences are being loaded from the API */
	const [notifLoading, setNotifLoading] = useState(true);

	/** Company options fetched from GET /companies for multi-select dropdowns */
	const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
	/** User options fetched from GET /users for multi-select dropdowns */
	const [userOptions, setUserOptions] = useState<UserOption[]>([]);

	/** Refs to each open watch-list dropdown container, keyed by field, for click-outside detection. */
	const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

	/* ============================================
	 * NOTIFICATION PREFERENCES LOADING
	 * ============================================ */

	/**
	 * Fetches notification preferences, companies, and users on mount.
	 * Sets watch-list ON states based on whether arrays are non-empty.
	 */
	useEffect(() => {
		const loadNotificationData = async () => {
			setNotifLoading(true);
			try {
				/* Fetch notification preferences, companies, and users in parallel */
				const [prefsRes, companiesRes, usersRes] = await Promise.all([
					api.get<any>('/auth/notification-preferences'),
					api.get<any>('/companies'),
					api.get<any>('/users'),
				]);

				/* Parse notification preferences from response. Booleans coerced;
				   arrays passed through (null when no subscription). */
				const prefs = prefsRes.data?.data ?? prefsRes.data ?? {};
				const next: NotificationPrefs = { ...DEFAULT_NOTIF_PREFS };
				(Object.keys(DEFAULT_NOTIF_PREFS) as (keyof NotificationPrefs)[]).forEach((key) => {
					if (NOTIF_ARRAY_FIELDS.includes(key as NotifArrayField)) {
						(next as any)[key] = prefs[key] ?? null;
					} else {
						(next as any)[key] = Boolean(prefs[key]);
					}
				});
				setNotifPrefs(next);

				/* Derive watch-list toggle states from whether arrays have values */
				setWatchOn(
					Object.fromEntries(
						NOTIF_ARRAY_FIELDS.map((f) => [f, Array.isArray(prefs[f]) && prefs[f].length > 0]),
					) as Record<NotifArrayField, boolean>,
				);

				/* Parse companies for dropdown options */
				const compData = Array.isArray(companiesRes.data) ? companiesRes.data : (companiesRes.data?.data ?? []);
				setCompanyOptions(compData.map((c: any) => ({ id: Number(c.id ?? c.company_id), name: c.name })));

				/* Parse users for dropdown options. Sorted A-Z by full display
				   name so the assigned-users multi-select is predictable; the
				   /users endpoint does not guarantee an order. */
				const userData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data ?? []);
				const userOpts: UserOption[] = userData.map((u: any) => ({ id: Number(u.id), first_name: u.first_name, last_name: u.last_name }));
				userOpts.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, undefined, { sensitivity: 'base' }));
				setUserOptions(userOpts);
			} catch {
				/* Silently fail — notification controls will show defaults */
			} finally {
				setNotifLoading(false);
			}
		};
		loadNotificationData();
	}, []);

	/**
	 * Closes the open watch-list dropdown when clicking outside of it.
	 * Only one dropdown is open at a time (openDropdown), so we just check
	 * that single container's ref.
	 */
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (!openDropdown) return;
			const el = dropdownRefs.current[openDropdown];
			if (el && !el.contains(e.target as Node)) {
				setOpenDropdown(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [openDropdown]);

	/* ============================================
	 * EVENT HANDLERS
	 * ============================================ */

	/**
	 * Handles changes to the personal info form inputs.
	 * Uses the input's name attribute as the key into ProfileFormData.
	 */
	const handleProfileChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Auto-format phone numbers as (###) ###-####
		const formatted = name === 'phone' ? formatPhone(value) : value;
		setProfileForm((prev) => ({ ...prev, [name]: formatted }));
		// Clear any previous message when user starts editing
		setProfileMessage(null);
	}, []);

	/**
	 * Submits the personal info form via PATCH /auth/profile.
	 * Sends only the changed fields (first_name, last_name, phone).
	 */
	const handleProfileSave = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setProfileSaving(true);
		setProfileMessage(null);

		try {
			await api.patch('/auth/profile', {
				first_name: profileForm.firstName,
				last_name: profileForm.lastName,
				phone: profileForm.phone,
				timezone: profileForm.timezone,
			});
			// Update the user in auth context so nav/sidebar reflect the new name
			// and timezone preference takes effect immediately for scheduling forms
			updateUser({ firstName: profileForm.firstName, lastName: profileForm.lastName, phone: profileForm.phone, timezone: profileForm.timezone });
			setProfileMessage({ type: 'success', text: 'Profile updated successfully.' });
		} catch (err: any) {
			// Extract validation errors or fallback to generic message
			const errorText = err?.data?.message || 'Failed to update profile. Please try again.';
			setProfileMessage({ type: 'error', text: errorText });
		} finally {
			setProfileSaving(false);
		}
	}, [profileForm, updateUser]);

	/**
	 * Handles changes to the password form inputs.
	 * Uses the input's name attribute as the key into PasswordFormData.
	 */
	const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setPasswordForm((prev) => ({ ...prev, [name]: value }));
		// Clear any previous message when user starts editing
		setPasswordMessage(null);
	}, []);

	/**
	 * Submits the change password form via POST /auth/change-password.
	 * Validates password policy compliance and confirmation match before sending.
	 */
	const handlePasswordSave = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setPasswordMessage(null);

		// Client-side validation: check all password rules pass
		const allRulesPass = PASSWORD_RULES.every((rule) => rule.test(passwordForm.newPassword));
		if (!allRulesPass) {
			setPasswordMessage({ type: 'error', text: 'New password does not meet all requirements.' });
			return;
		}

		// Client-side validation: passwords must match
		if (passwordForm.newPassword !== passwordForm.confirmPassword) {
			setPasswordMessage({ type: 'error', text: 'New password and confirmation do not match.' });
			return;
		}

		setPasswordSaving(true);

		try {
			await api.post('/auth/change-password', {
				current_password: passwordForm.currentPassword,
				new_password: passwordForm.newPassword,
				new_password_confirmation: passwordForm.confirmPassword,
			});
			setPasswordMessage({ type: 'success', text: 'Password changed successfully.' });
			// Clear the password form after successful change
			setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
		} catch (err: any) {
			const errorText = err?.data?.message || 'Failed to change password. Please check your current password.';
			setPasswordMessage({ type: 'error', text: errorText });
		} finally {
			setPasswordSaving(false);
		}
	}, [passwordForm]);

	/**
	 * Saves the notification preferences via PATCH /auth/notification-preferences.
	 * Sends every bool field plus every watch-list array — arrays whose toggle is
	 * OFF are sent as null to clear the subscription.
	 */
	const handleNotifSave = useCallback(async () => {
		setNotifSaving(true);
		setNotifMessage(null);

		try {
			const payload: Record<string, any> = { ...notifPrefs };
			NOTIF_ARRAY_FIELDS.forEach((field) => {
				payload[field] = watchOn[field] ? (notifPrefs[field] ?? []) : null;
			});

			await api.patch('/auth/notification-preferences', payload);
			setNotifMessage({ type: 'success', text: 'Notification preferences saved.' });
		} catch (err: any) {
			const errorText = err?.data?.message || 'Failed to save notification preferences.';
			setNotifMessage({ type: 'error', text: errorText });
		} finally {
			setNotifSaving(false);
		}
	}, [notifPrefs, watchOn]);

	/** Flip a boolean preference field and clear any save message. */
	const toggleBool = useCallback((field: NotifBoolField) => {
		setNotifPrefs((prev) => ({ ...prev, [field]: !prev[field] }));
		setNotifMessage(null);
	}, []);

	/** Flip a watch-list ON/OFF and clear any save message. */
	const toggleWatch = useCallback((field: NotifArrayField) => {
		setWatchOn((prev) => ({ ...prev, [field]: !prev[field] }));
		setNotifMessage(null);
	}, []);

	/**
	 * Toggles an item (company or user ID) in a watch-list array field.
	 * If the ID is already present, removes it. If absent, adds it.
	 */
	const toggleArrayItem = useCallback((field: NotifArrayField, id: number) => {
		setNotifPrefs((prev) => {
			const current = prev[field] ?? [];
			const exists = current.includes(id);
			return {
				...prev,
				[field]: exists ? current.filter((v) => v !== id) : [...current, id],
			};
		});
		/* Clear any previous notification message when editing */
		setNotifMessage(null);
	}, []);

	/* ============================================
	 * DERIVED VALUES
	 * ============================================ */

	// Full display name
	const fullName = user ? `${user.firstName} ${user.lastName}` : 'Loading...';

	// Role badge color classes
	const roleBadgeClass = ROLE_BADGE_COLORS[user?.role ?? ''] || 'bg-gray-100 text-gray-800';

	// Human-readable role label
	const roleLabel = user?.role ? user.role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

	// Human-readable auth provider label
	const authProviderLabel = user?.authProvider === 'o365'
		? 'Office 365 SSO'
		: user?.authProvider === 'google'
			? 'Google SSO'
			: 'Email / Password';

	/**
	 * Render a single notification watch-list row (switch + multi-select dropdown).
	 * Inlined (vs. a child component) so the dropdown ref + open state stay simple.
	 */
	const renderWatchRow = (row: Extract<NotifRow, { type: 'watch' }>) => {
		const selected = notifPrefs[row.field] ?? [];
		const on = watchOn[row.field] ?? false;
		const isCompany = row.options === 'company';
		const options: (CompanyOption | UserOption)[] = isCompany ? companyOptions : userOptions;
		const noun = isCompany ? 'company(ies)' : 'user(s)';
		const placeholder = isCompany ? 'Select companies...' : 'Select users...';

		return (
			<div key={row.field}>
				<div className="profile-page__watch-toggle flex items-center justify-between">
					<span className="text-14px text-brand-text font-medium">{row.label}</span>
					<ToggleSwitch checked={on} onChange={() => toggleWatch(row.field)} label={row.label} />
				</div>
				{/* Multi-select dropdown — only visible when the toggle is ON */}
				{on && (
					<div className="mt-8px" ref={(el) => { dropdownRefs.current[row.field] = el; }}>
						<div className="relative">
							{/* Dropdown trigger button showing selected count */}
							<button type="button" onClick={() => setOpenDropdown((prev) => (prev === row.field ? null : row.field))} className="w-full px-12px py-8px border border-brand-border rounded-md text-14px text-brand-text text-left bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary">
								{selected.length === 0 ? placeholder : `${selected.length} ${noun} selected`}
							</button>
							{/* Dropdown options panel */}
							{openDropdown === row.field && (
								<div className="absolute z-10 mt-4px w-full bg-white border border-brand-border rounded-md shadow-lg max-h-[200px] overflow-y-auto">
									{options.map((opt) => {
										const isChecked = selected.includes(opt.id);
										const optLabel = isCompany ? (opt as CompanyOption).name : `${(opt as UserOption).first_name} ${(opt as UserOption).last_name}`;
										return (
											<Checkbox key={opt.id} checked={isChecked} onChange={() => toggleArrayItem(row.field, opt.id)} label={optLabel} className="profile-page__watch-option w-full items-center px-12px hover:bg-brand-surface" dataRef="page-profile-page-watch-option" />
										);
									})}
									{options.length === 0 && (
										<p className="px-12px py-8px text-12px text-brand-text-subtle italic">No {isCompany ? 'companies' : 'users'} available.</p>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		);
	};

	/* ============================================
	 * RENDER
	 * ============================================ */

	return (
		<PageContainer block="profile-page" dataRef="page-profile-page" width="narrow">
			{/* === SECTION 1: Profile Header === */}
			<div className="bg-white rounded-lg shadow p-24px mb-24px">
				<div className="flex items-center gap-16px">
					{/* Avatar with upload/crop functionality */}
					<AvatarUpload
						currentPhotoUrl={user?.profilePhotoUrl}
						userName={fullName}
						size={96}
						onSave={async (base64) => {
							// Upload the cropped avatar to the dedicated profile photo endpoint
							const res = await api.post<{ url: string; thumb_url: string }>('/auth/avatar', {
								photo: base64,
							});

							// The API returns the full-size URL and the 50x50 thumbnail URL.
							// Both may be host-relative (/storage/avatars/…), so resolve them
							// against the configured API host rather than assuming a port.
							const fullUrl = resolveStorageUrl(res.data.url);
							const thumbUrl = resolveStorageUrl(res.data.thumb_url);

							// Update BOTH fields. The top bar and sidebar render
							// profilePhotoThumbUrl — updating only profilePhotoUrl left the
							// header showing the old initial-letter avatar, which is what made
							// the upload look like it had silently failed.
							updateUser({ profilePhotoUrl: fullUrl, profilePhotoThumbUrl: thumbUrl });
						}}
					/>

					{/* Name, role badge, and email.
					    min-w-0 + break-all because an address like
					    superadmin@gbrandslimited.com is a single unbreakable word:
					    without them the block refused to shrink and took the whole
					    page sideways on a 320px handset. */}
					<div className="profile-page__identity min-w-0">
						<div className="flex flex-wrap items-center gap-8px">
							<h1 className="text-20px font-bold font-heading text-brand-text">{fullName}</h1>
							{/* Role badge with color coding by role type */}
							<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${roleBadgeClass}`}>
								{roleLabel}
							</span>
						</div>
						<p className="profile-page__email text-14px text-brand-text-muted mt-4px break-all">{user?.email}</p>
					</div>
				</div>
			</div>

			{/* === SECTION 2: Personal Info Form === */}
			<div className="bg-white rounded-lg shadow p-24px mb-24px">
				<h2 className="text-18px font-bold font-heading text-brand-text mb-16px">Personal Information</h2>

				{/* Success/error banner for profile save */}
				{profileMessage && (
					<Alert variant={profileMessage.type === 'success' ? 'success' : 'error'} className="mb-16px">{profileMessage.text}</Alert>
				)}

				<form onSubmit={handleProfileSave}>
					{/* Responsive grid: 1 column on mobile, 2 on tablet+ */}
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
						{/* First Name field */}
						<div>
							<label htmlFor="firstName" className="block text-14px font-medium text-brand-text mb-4px">First Name</label>
							<input type="text" id="firstName" name="firstName" value={profileForm.firstName} onChange={handleProfileChange} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="Enter first name" />
						</div>

						{/* Last Name field */}
						<div>
							<label htmlFor="lastName" className="block text-14px font-medium text-brand-text mb-4px">Last Name</label>
							<input type="text" id="lastName" name="lastName" value={profileForm.lastName} onChange={handleProfileChange} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="Enter last name" />
						</div>

						{/* Phone field */}
						<div>
							<label htmlFor="phone" className="block text-14px font-medium text-brand-text mb-4px">Phone</label>
							<input type="tel" id="phone" name="phone" value={profileForm.phone} onChange={handleProfileChange} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="(555) 123-4567" />
						</div>

						{/* Local Timezone preference — used as default when scheduling work orders/inspections */}
						<div>
							<label htmlFor="timezone" className="block text-14px font-medium text-brand-text mb-4px">Local Timezone</label>
							<select id="timezone" name="timezone" value={profileForm.timezone} onChange={handleProfileChange} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-white min-h-44px">
								{TIMEZONE_OPTIONS.map((tz) => (
									<option key={tz.value} value={tz.value}>{tz.label}</option>
								))}
							</select>
							<p className="mt-4px text-12px text-brand-text-subtle">Used as the default timezone when scheduling work orders and inspections.</p>
						</div>
					</div>

					{/* Save button — aligned right */}
					<div className="mt-16px flex justify-end">
						<Button type="submit" variant="primary" size="sm" disabled={profileSaving}>
							{profileSaving ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</form>
			</div>

			{/* === SECTION 3: Change Password === */}
			<div className="bg-white rounded-lg shadow p-24px mb-24px">
				<h2 className="text-18px font-bold font-heading text-brand-text mb-16px">Change Password</h2>

				{/* Success/error banner for password change */}
				{passwordMessage && (
					<Alert variant={passwordMessage.type === 'success' ? 'success' : 'error'} className="mb-16px">{passwordMessage.text}</Alert>
				)}

				<form onSubmit={handlePasswordSave}>
					<div className="space-y-16px">
						{/* Current Password field with show/hide toggle */}
						<div>
							<label htmlFor="currentPassword" className="block text-14px font-medium text-brand-text mb-4px">Current Password</label>
							<div className="relative">
								<input type={showCurrentPassword ? 'text' : 'password'} id="currentPassword" name="currentPassword" value={passwordForm.currentPassword} onChange={handlePasswordChange} className="w-full px-12px py-8px pr-40px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="Enter current password" />
								{/* Toggle visibility button positioned inside the input */}
								<button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-8px top-1/2 -translate-y-1/2 inline-flex items-center min-h-44px text-brand-text-subtle hover:text-brand-text text-12px font-medium px-8px">
									{showCurrentPassword ? 'Hide' : 'Show'}
								</button>
							</div>
						</div>

						{/* New Password field with show/hide toggle */}
						<div>
							<label htmlFor="newPassword" className="block text-14px font-medium text-brand-text mb-4px">New Password</label>
							<div className="relative">
								<input type={showNewPassword ? 'text' : 'password'} id="newPassword" name="newPassword" value={passwordForm.newPassword} onChange={handlePasswordChange} className="w-full px-12px py-8px pr-40px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="Enter new password" />
								<button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-8px top-1/2 -translate-y-1/2 inline-flex items-center min-h-44px text-brand-text-subtle hover:text-brand-text text-12px font-medium px-8px">
									{showNewPassword ? 'Hide' : 'Show'}
								</button>
							</div>

							{/* Password requirements checklist — only shown when user starts typing */}
							{passwordForm.newPassword.length > 0 && (
								<ul className="mt-8px space-y-4px">
									{PASSWORD_RULES.map((rule) => {
										const passes = rule.test(passwordForm.newPassword);
										return (
											<li key={rule.label} className={`text-12px flex items-center gap-4px ${passes ? 'text-green-600' : 'text-brand-text-subtle'}`}>
												{/* Checkmark or circle icon to indicate pass/fail */}
												<span>{passes ? '✓' : '○'}</span>
												<span>{rule.label}</span>
											</li>
										);
									})}
								</ul>
							)}
						</div>

						{/* Confirm Password field with show/hide toggle */}
						<div>
							<label htmlFor="confirmPassword" className="block text-14px font-medium text-brand-text mb-4px">Confirm New Password</label>
							<div className="relative">
								<input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={passwordForm.confirmPassword} onChange={handlePasswordChange} className="w-full px-12px py-8px pr-40px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="Re-enter new password" />
								<button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-8px top-1/2 -translate-y-1/2 inline-flex items-center min-h-44px text-brand-text-subtle hover:text-brand-text text-12px font-medium px-8px">
									{showConfirmPassword ? 'Hide' : 'Show'}
								</button>
							</div>
							{/* Mismatch warning — shown when both fields have content and don't match */}
							{passwordForm.confirmPassword.length > 0 && passwordForm.newPassword !== passwordForm.confirmPassword && (
								<p className="mt-4px text-12px text-red-600">Passwords do not match.</p>
							)}
						</div>
					</div>

					{/* Change Password button — aligned right */}
					<div className="mt-16px flex justify-end">
						<Button type="submit" variant="primary" size="sm" disabled={passwordSaving}>
							{passwordSaving ? 'Changing...' : 'Change Password'}
						</Button>
					</div>
				</form>
			</div>

			{/* === SECTION 4: Notification Controls === */}
			<div className="bg-white rounded-lg shadow p-24px mb-24px">
				<h2 className="text-18px font-bold font-heading text-brand-text mb-16px">Notification Controls</h2>
				<p className="text-14px text-brand-text-muted mb-16px">Choose which email notifications you receive for Work Order and Inspection activity.</p>

				{/* Success/error banner for notification preferences save */}
				{notifMessage && (
					<Alert variant={notifMessage.type === 'success' ? 'success' : 'error'} className="mb-16px">{notifMessage.text}</Alert>
				)}

				{/* Loading state while fetching notification preferences */}
				{notifLoading ? (
					<div className="flex items-center justify-center py-24px">
						<p className="text-brand-text-subtle text-14px">Loading notification preferences...</p>
					</div>
				) : (
					<div className="space-y-24px">
						{/* One section per record type (Work Orders, Inspections), rendered
						    from NOTIF_GROUPS so the two stay structurally identical. */}
						{NOTIF_GROUPS.map((group) => (
							<div key={group.title}>
								<h3 className="text-14px font-bold font-heading text-brand-text-muted uppercase tracking-wide mb-12px">{group.title}</h3>
								<div className="space-y-20px">
									{group.rows.map((row) =>
										row.type === 'bool' ? (
											<NotifToggle key={row.field} label={row.label} checked={!!notifPrefs[row.field]} onChange={() => toggleBool(row.field)} />
										) : (
											renderWatchRow(row)
										),
									)}
								</div>
							</div>
						))}

						{/* Save Notification Preferences button — aligned right */}
						<div className="flex justify-end pt-8px">
							<Button type="button" variant="primary" size="sm" onClick={handleNotifSave} disabled={notifSaving}>
								{notifSaving ? 'Saving...' : 'Save Preferences'}
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* === SECTION 5: Account Info (Read-Only) === */}
			<div className="bg-white rounded-lg shadow p-24px">
				<h2 className="text-18px font-bold font-heading text-brand-text mb-16px">Account Information</h2>

				{/* Read-only info grid: label-value pairs in a 2-column layout */}
				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
					{/* Role */}
					<div>
						<p className="text-12px font-medium text-brand-text-subtle uppercase tracking-wide">Role</p>
						<p className="mt-4px text-14px text-brand-text font-medium">{roleLabel}</p>
					</div>

					{/* Company */}
					<div>
						<p className="text-12px font-medium text-brand-text-subtle uppercase tracking-wide">Company</p>
						<p className="mt-4px text-14px text-brand-text font-medium">{user?.companyId || 'Not assigned'}</p>
					</div>

					{/* Authentication Provider */}
					<div>
						<p className="text-12px font-medium text-brand-text-subtle uppercase tracking-wide">Auth Provider</p>
						<p className="mt-4px text-14px text-brand-text font-medium">{authProviderLabel}</p>
					</div>

					{/* Last Login — would need to be populated from the API response */}
					<div>
						<p className="text-12px font-medium text-brand-text-subtle uppercase tracking-wide">Last Login</p>
						<p className="mt-4px text-14px text-brand-text font-medium">--</p>
					</div>

					{/* Member Since — would need to be populated from the API response */}
					<div>
						<p className="text-12px font-medium text-brand-text-subtle uppercase tracking-wide">Member Since</p>
						<p className="mt-4px text-14px text-brand-text font-medium">--</p>
					</div>
				</div>
			</div>
		</PageContainer>
	);
};

export default ProfilePage;
